import React, { useState, useEffect, useRef } from 'react';
import ReactQuill from 'react-quill';
import { Button, ButtonGroup } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMicrophone, faStop, faWaveSquare } from '@fortawesome/free-solid-svg-icons';
import 'react-quill/dist/quill.snow.css';

const QuillEditor = ({ onQuilEditorChange, value }) => {
  const [isListening, setIsListening] = useState(false);
  const [interimText, setInterimText] = useState(''); // Holds real-time partial speech
  const quillRef = useRef(null);
  const recognitionRef = useRef(null);

  useEffect(() => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (SpeechRecognition) {
      const recognition = new SpeechRecognition();
      recognition.continuous = true;
      recognition.interimResults = true; // Enables real-time typing
      recognition.lang = 'en-US';

      recognition.onresult = (event) => {
        let finalTranscript = '';
        let interimTranscript = '';

        for (let i = event.resultIndex; i < event.results.length; ++i) {
          if (event.results[i].isFinal) {
            finalTranscript += event.results[i][0].transcript;
          } else {
            interimTranscript += event.results[i][0].transcript;
          }
        }

        // 1. Update the temporary "real-time" visual text
        setInterimText(interimTranscript);

        // 2. If we have final text, inject it into Quill
        if (finalTranscript) {
          const quill = quillRef.current.getEditor();
          const range = quill.getSelection();
          const position = range ? range.index : quill.getLength();

          quill.insertText(position, finalTranscript + ' ');
          onQuilEditorChange(quill.root.innerHTML);
          setInterimText(''); // Clear interim once finalized
        }
      };

      recognition.onerror = (event) => {
        console.error("Speech Error:", event.error);
        setIsListening(false);
      };

      recognition.onend = () => setIsListening(false);
      recognitionRef.current = recognition;
    }
  }, [onQuilEditorChange]);

  const startListening = () => {
    if (recognitionRef.current) {
      recognitionRef.current.start();
      setIsListening(true);
    }
  };

  const stopListening = () => {
    if (recognitionRef.current) {
      recognitionRef.current.stop();
      setIsListening(false);
      setInterimText('');
    }
  };

  const imageHandler = () => {
    const input = document.createElement('input');
    input.setAttribute('type', 'file');
    input.setAttribute('accept', 'image/*');
    input.click();

    input.onchange = async () => {
      const file = input.files[0];
      if (/^image\//.test(file.type)) {
        const reader = new FileReader();
        reader.onload = (e) => {
          const img = new Image();
          img.src = e.target.result;
          img.onload = () => {
            const canvas = document.createElement('canvas');
            const MAX_WIDTH = 200;
            const MAX_HEIGHT = 200;
            let width = img.width;
            let height = img.height;

            if (width > height) {
              if (width > MAX_WIDTH) {
                height *= MAX_WIDTH / width;
                width = MAX_WIDTH;
              }
            } else {
              if (height > MAX_HEIGHT) {
                width *= MAX_HEIGHT / height;
                height = MAX_HEIGHT;
              }
            }

            canvas.width = width;
            canvas.height = height;
            const ctx = canvas.getContext('2d');
            ctx.drawImage(img, 0, 0, width, height);

            // Compress to JPEG with 0.5 quality
            const compressedBase64 = canvas.toDataURL('image/jpeg', 0.3);

            const quill = quillRef.current.getEditor();
            const range = quill.getSelection();
            quill.insertEmbed(range.index, 'image', compressedBase64);
          };
        };
        reader.readAsDataURL(file);
      }
    };
  };

  const modules = React.useMemo(() => ({
    toolbar: {
      container: [
        [{ header: '1' }, { header: '2' }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ list: 'ordered' }, { list: 'bullet' }],
        ['link', 'image'],
        ['clean']
      ],
      handlers: {
        image: imageHandler
      }
    }
  }), []);

  return (
    <div className="quill-editor-wrapper">
      <div className="d-flex justify-content-between align-items-center mb-2">
        <label className="new-ticket-label mb-0">Description</label>

        <ButtonGroup size="sm">
          <Button
            variant="outline-success"
            onClick={startListening}
            disabled={isListening}
          >
            <FontAwesomeIcon icon={faMicrophone} className="me-1" /> Start Dictation
          </Button>
          <Button
            variant="outline-danger"
            onClick={stopListening}
            disabled={!isListening}
          >
            <FontAwesomeIcon icon={faStop} className="me-1" /> Stop
          </Button>
        </ButtonGroup>
      </div>

      {/* Real-time "Floating" Preview */}
      {isListening && (
        <div className="p-2 mb-2 border rounded bg-light text-muted small" style={{ borderStyle: 'dashed' }}>
          <FontAwesomeIcon icon={faWaveSquare} className="text-primary me-2" />
          <strong>Listening:</strong> {interimText || "..."}
        </div>
      )}

      <ReactQuill
        ref={quillRef}
        theme="snow"
        modules={modules}
        value={value || ''}
        onChange={onQuilEditorChange}
        placeholder="Type or use the microphone to dictate..."
      />
    </div>
  );
};

export default QuillEditor;